import { getSafeExternalUrl, sanitizePlainText } from "./security";

export const CAMPAIGN_STATUSES = {
  DRAFT: "draft",
  LIVE: "live",
  PAUSED: "paused",
  CLOSING: "closing",
  CLOSED: "closed",
  COMPLETED: "completed",
};

export const SUBMISSION_STATUSES = {
  PENDING: "pending_review",
  REVISION: "needs_revision",
  APPROVED: "approved",
  REJECTED: "rejected",
  DISPUTED: "disputed",
};

export const PAYOUT_STATUSES = {
  ESTIMATED: "estimated",
  PENDING: "pending",
  HELD: "held",
  AVAILABLE: "available",
  PROCESSING: "processing",
  PAID: "paid",
  FAILED: "failed",
};

export const PLATFORM_PAYOUT_RULES = {
  EACH_PLATFORM: "each_platform",
  HIGHEST_PLATFORM: "highest_platform_only",
};

export const SUPPORTED_CLIP_PLATFORMS = ["TikTok", "YouTube Shorts", "Instagram Reels"];
export const SUPPORTED_STREAM_PLATFORMS = ["Twitch", "Kick"];
export const CAMPAIGN_CATEGORIES = ["Gaming", "IRL"];
export const KRE8_CAMPAIGN_FEE_BPS = 1000;
export const CLIPPER_FEE_BPS = 500;
export const MINIMUM_WITHDRAWAL_CENTS = 1000;
export const MINIMUM_CAMPAIGN_BUDGET_CENTS = 2000;
export const MINIMUM_PLATFORM_PAYOUT_CENTS = 2000;
export const DEFAULT_PLATFORM_MAX_PAYOUT_CENTS = 30000;
export const STRIPE_CARD_RATE_BPS = 290;
export const STRIPE_FIXED_FEE_CENTS = 30;

export const PLATFORM_PAYOUT_LIMIT_KEYS = {
  TikTok: "tiktok",
  "YouTube Shorts": "youtube_shorts",
  "Instagram Reels": "instagram_reels",
};

export function dollarsToCents(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return Math.round(amount * 100);
}

export function centsToDollars(cents = 0) {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return 0;
  return amount / 100;
}

export function formatCurrency(cents = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(centsToDollars(cents));
}

export function formatViews(views = 0) {
  const value = Number(views);
  if (!Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

export function calculatePayoutCents(views, rpmCents) {
  const safeViews = Math.max(0, Math.floor(Number(views) || 0));
  const safeRpm = Math.max(0, Math.floor(Number(rpmCents) || 0));
  return Math.round((safeViews / 1000) * safeRpm);
}

export function calculateBoundedPayoutCents(views, rpmCents, maxPayoutCents = null, minPayoutCents = null) {
  const calculatedPayoutCents = calculatePayoutCents(views, rpmCents);
  const flooredPayoutCents = minPayoutCents == null
    ? calculatedPayoutCents
    : Math.max(calculatedPayoutCents, Math.max(0, Math.floor(Number(minPayoutCents) || 0)));
  return maxPayoutCents == null
    ? flooredPayoutCents
    : Math.min(flooredPayoutCents, Math.max(0, Math.floor(Number(maxPayoutCents) || 0)));
}

export function calculateClipperPayout(views, rpmCents, maxPayoutCents = null, minPayoutCents = null) {
  const grossAmountCents = calculateBoundedPayoutCents(
    views,
    rpmCents,
    maxPayoutCents,
    minPayoutCents
  );
  const platformFeeCents = Math.round((grossAmountCents * CLIPPER_FEE_BPS) / 10000);
  return {
    grossAmountCents,
    platformFeeCents,
    amountCents: grossAmountCents - platformFeeCents,
  };
}

export function getPlatformPayoutLimitKey(platform) {
  return PLATFORM_PAYOUT_LIMIT_KEYS[platform] || String(platform || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function createDefaultPlatformPayoutLimitInputs() {
  return SUPPORTED_CLIP_PLATFORMS.reduce((limits, platform) => {
    limits[getPlatformPayoutLimitKey(platform)] = {
      platform,
      maxPayout: String(centsToDollars(DEFAULT_PLATFORM_MAX_PAYOUT_CENTS)),
    };
    return limits;
  }, {});
}

const isPositiveInteger = (value) =>
  Number.isSafeInteger(Number(value)) && Number(value) > 0;

export function getCampaignPlatformPayoutLimit(campaign = {}, platform) {
  const key = getPlatformPayoutLimitKey(platform);
  const stored = campaign.platformPayoutLimits?.[key] || campaign.platformPayoutLimits?.[platform] || {};
  const minPayoutCents = isPositiveInteger(stored.minPayoutCents)
    ? Number(stored.minPayoutCents)
    : isPositiveInteger(campaign.minPayoutCents)
      ? Number(campaign.minPayoutCents)
      : null;
  const maxPayoutCents = isPositiveInteger(stored.maxPayoutCents)
    ? Number(stored.maxPayoutCents)
    : isPositiveInteger(campaign.maxPayoutPerSubmissionCents)
      ? Number(campaign.maxPayoutPerSubmissionCents)
      : null;
  return {
    platform,
    key,
    minPayoutCents,
    maxPayoutCents,
  };
}

export function getSubmissionPayoutLimit(campaign = {}, submission = {}) {
  const campaignLimit = getCampaignPlatformPayoutLimit(campaign, submission.platform);
  return {
    platform: submission.platform,
    minPayoutCents: isPositiveInteger(submission.minPayoutCents)
      ? Number(submission.minPayoutCents)
      : campaignLimit.minPayoutCents,
    maxPayoutCents: isPositiveInteger(submission.maxPayoutCents)
      ? Number(submission.maxPayoutCents)
      : campaignLimit.maxPayoutCents,
  };
}

export function calculateCampaignFundingBreakdown(budgetCents) {
  const budget = Math.max(0, Math.round(Number(budgetCents) || 0));
  const platformFeeCents = Math.round((budget * KRE8_CAMPAIGN_FEE_BPS) / 10000);
  const netBeforeProcessing = budget + platformFeeCents;
  const totalChargeCents = Math.ceil(
    (netBeforeProcessing + STRIPE_FIXED_FEE_CENTS) /
      (1 - STRIPE_CARD_RATE_BPS / 10000)
  );

  return {
    budgetCents: budget,
    platformFeeCents,
    processingFeeCents: Math.max(0, totalChargeCents - netBeforeProcessing),
    totalChargeCents,
  };
}

export function normalizeExternalUrl(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  try {
    const url = new URL(/^(?:https?:)?\/\//i.test(trimmed) ? trimmed.replace(/^\/\//, "https://") : `https://${trimmed}`);
    if (url.protocol === "http:") url.protocol = "https:";
    return getSafeExternalUrl(url.href);
  } catch {
    return "";
  }
}

export function normalizeVodLinks(value) {
  const links = Array.isArray(value) ? value : String(value || "").split(/\r?\n|,/);
  return [...new Set(links.map(normalizeExternalUrl).filter(Boolean))].slice(0, 10);
}

export function validateCampaignInput(input) {
  const errors = {};
  const title = sanitizePlainText(input.title, { maxLength: 120 });
  const description = sanitizePlainText(input.description, { maxLength: 5000 });
  const vodLinks = normalizeVodLinks(input.vodLinks);
  const budgetCents = dollarsToCents(input.budget);
  const rpmCents = dollarsToCents(input.rpm);
  const submissionWindowHours = Number(input.submissionWindowHours);
  const trackingWindowDays = Number(input.trackingWindowDays);
  const campaignDurationDays = Number(input.campaignDurationDays || 60);
  const category = CAMPAIGN_CATEGORIES.includes(input.category) ? input.category : "";
  const streamPlatform = SUPPORTED_STREAM_PLATFORMS.includes(input.streamPlatform) ? input.streamPlatform : "";
  const game = sanitizePlainText(input.game, { maxLength: 100 });
  const targetPlatforms = Array.isArray(input.targetPlatforms)
    ? input.targetPlatforms.filter((platform) => SUPPORTED_CLIP_PLATFORMS.includes(platform))
    : [];
  const platformPayoutLimits = {};
  targetPlatforms.forEach((platform) => {
    const key = getPlatformPayoutLimitKey(platform);
    const rawLimit = input.platformPayoutLimits?.[key] || input.platformPayoutLimits?.[platform];
    const limitInput = rawLimit || {};
    const rawMaxPayout = limitInput.maxPayout ?? limitInput.max ?? rawLimit;
    const maxPayoutCents = rawLimit
      ? limitInput.maxPayoutCents != null
        ? Math.round(Number(limitInput.maxPayoutCents))
        : dollarsToCents(rawMaxPayout)
      : DEFAULT_PLATFORM_MAX_PAYOUT_CENTS;
    platformPayoutLimits[key] = {
      platform,
      minPayoutCents: MINIMUM_PLATFORM_PAYOUT_CENTS,
      maxPayoutCents,
    };
    if (maxPayoutCents < MINIMUM_PLATFORM_PAYOUT_CENTS) {
      errors[`platformPayoutLimits.${key}`] = `Set a max of at least ${formatCurrency(MINIMUM_PLATFORM_PAYOUT_CENTS)} for ${platform}.`;
    }
  });
  const resources = Array.isArray(input.resources)
    ? input.resources
        .map((resource) => ({
          name: sanitizePlainText(resource?.name, { maxLength: 180 }),
          url: normalizeExternalUrl(resource?.url),
        }))
        .filter((resource) => resource.name && resource.url)
        .slice(0, 10)
    : [];

  if (title.length < 5) errors.title = "Use at least 5 characters for the campaign title.";
  if (description.length < 20) errors.description = "Give clippers a brief of at least 20 characters.";
  if (!streamPlatform) errors.streamPlatform = "Choose Twitch or Kick.";
  if (vodLinks.length === 0) errors.vodLinks = "Add at least one valid Twitch or Kick VOD or source link.";
  if (budgetCents < MINIMUM_CAMPAIGN_BUDGET_CENTS) errors.budget = `Campaign budgets must be at least ${formatCurrency(MINIMUM_CAMPAIGN_BUDGET_CENTS)}.`;
  if (rpmCents < 1) errors.rpm = "RPM must be at least $0.01.";
  if (![24, 48, 72, 168].includes(submissionWindowHours)) errors.submissionWindowHours = "Choose a valid submission window.";
  if (![7, 14, 30].includes(trackingWindowDays)) errors.trackingWindowDays = "Choose a valid tracking window.";
  if (campaignDurationDays !== 60) errors.campaignDurationDays = "Campaigns must run for at least 60 days.";
  if (!category) errors.category = "Choose Gaming or IRL.";
  if (category === "Gaming" && game.length < 2) errors.game = "Add the game for this campaign.";
  if (targetPlatforms.length === 0) errors.targetPlatforms = "Choose at least one allowed platform.";
  if (!input.bannerUrl) errors.bannerUrl = "Upload a campaign banner.";

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    value: {
      title,
      description,
      category,
      streamPlatform,
      game: category === "Gaming" ? game : "",
      bannerUrl: String(input.bannerUrl || ""),
      vodLinks,
      momentsToWatch: sanitizePlainText(input.momentsToWatch, { maxLength: 5000 }),
      styleNotes: sanitizePlainText(input.styleNotes, { maxLength: 5000 }),
      targetPlatforms,
      resources,
      budgetCents,
      rpmCents,
      minPayoutCents: MINIMUM_PLATFORM_PAYOUT_CENTS,
      platformPayoutLimits,
      submissionWindowHours,
      trackingWindowDays,
      campaignDurationDays,
      platformPayoutRule: Object.values(PLATFORM_PAYOUT_RULES).includes(input.platformPayoutRule)
        ? input.platformPayoutRule
        : PLATFORM_PAYOUT_RULES.HIGHEST_PLATFORM,
    },
  };
}

export function campaignToEditableInput(campaign = {}, resources = []) {
  const editableResources = Array.isArray(resources)
    ? resources.map((resource) => ({
        name: String(resource?.name || ""),
        url: String(resource?.url || ""),
      }))
    : [];

  const targetPlatforms = Array.isArray(campaign.targetPlatforms) && campaign.targetPlatforms.length > 0
    ? campaign.targetPlatforms.filter((platform) => SUPPORTED_CLIP_PLATFORMS.includes(platform))
    : [...SUPPORTED_CLIP_PLATFORMS];
  const platformPayoutLimits = createDefaultPlatformPayoutLimitInputs();
  SUPPORTED_CLIP_PLATFORMS.forEach((platform) => {
    const limit = getCampaignPlatformPayoutLimit(campaign, platform);
    if (limit.maxPayoutCents != null) {
      platformPayoutLimits[limit.key] = {
        platform,
        maxPayout: String(centsToDollars(limit.maxPayoutCents)),
      };
    }
  });

  return {
    title: String(campaign.title || ""),
    description: String(campaign.description || ""),
    category: CAMPAIGN_CATEGORIES.includes(campaign.category) ? campaign.category : "Gaming",
    streamPlatform: SUPPORTED_STREAM_PLATFORMS.includes(campaign.streamPlatform)
      ? campaign.streamPlatform
      : "Twitch",
    game: String(campaign.game || ""),
    vodLinks: Array.isArray(campaign.vodLinks) ? campaign.vodLinks.join("\n") : String(campaign.vodLinks || ""),
    momentsToWatch: String(campaign.momentsToWatch || ""),
    styleNotes: String(campaign.styleNotes || ""),
    targetPlatforms,
    budget: String(centsToDollars(campaign.budgetCents || 0)),
    rpm: String(centsToDollars(campaign.rpmCents || 0)),
    submissionWindowHours: String(campaign.submissionWindowHours || 48),
    trackingWindowDays: String(campaign.trackingWindowDays || 30),
    campaignDurationDays: "60",
    platformPayoutRule: Object.values(PLATFORM_PAYOUT_RULES).includes(campaign.platformPayoutRule)
      ? campaign.platformPayoutRule
      : PLATFORM_PAYOUT_RULES.HIGHEST_PLATFORM,
    platformPayoutLimits,
    resources: editableResources.length > 0 ? editableResources : [{ name: "", url: "" }],
    bannerUrl: String(campaign.bannerUrl || ""),
  };
}

export function validateSubmissionInput(input) {
  const errors = {};
  const clipUrl = normalizeExternalUrl(input.clipUrl);
  const postedAt = input.postedAt ? new Date(input.postedAt) : null;
  const platform = String(input.platform || "");

  if (!clipUrl) errors.clipUrl = "Add a valid public clip URL.";
  if (!SUPPORTED_CLIP_PLATFORMS.includes(platform)) errors.platform = "Choose the platform where the clip was posted.";
  if (!postedAt || Number.isNaN(postedAt.getTime())) errors.postedAt = "Add the date and time the clip was posted.";
  if (postedAt && postedAt.getTime() > Date.now() + 5 * 60 * 1000) errors.postedAt = "The posted time cannot be in the future.";
  if (!input.originalWorkAccepted) errors.originalWorkAccepted = "Confirm that this is your original work.";
  if (!input.rulesAccepted) errors.rulesAccepted = "Accept the campaign rules before submitting.";

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    value: {
      clipUrl,
      platform,
      postedAt,
      sourceTimestamp: sanitizePlainText(input.sourceTimestamp, { maxLength: 50 }),
      caption: sanitizePlainText(input.caption, { maxLength: 2200 }),
    },
  };
}

export function validateGroupedSubmissionInput(input) {
  const errors = {};
  const posts = (Array.isArray(input.posts) ? input.posts : [])
    .filter((post) => post.enabled)
    .map((post) => ({
      platform: String(post.platform || ""),
      clipUrl: normalizeExternalUrl(post.clipUrl),
      postedAt: post.postedAt ? new Date(post.postedAt) : null,
      caption: sanitizePlainText(post.caption, { maxLength: 2200 }),
    }));

  if (!input.sourceVideoUrl) errors.sourceVideo = "Upload the source edit first.";
  if (posts.length < 1) errors.posts = "Add at least one platform link.";
  if (new Set(posts.map((post) => post.platform)).size !== posts.length) {
    errors.posts = "Add only one link for each platform.";
  }
  posts.forEach((post) => {
    if (!SUPPORTED_CLIP_PLATFORMS.includes(post.platform)) {
      errors.posts = "Choose a supported platform.";
    } else if (!post.clipUrl) {
      errors.posts = `Add a valid ${post.platform} link.`;
    } else if (!post.postedAt || Number.isNaN(post.postedAt.getTime())) {
      errors.posts = `Add the posted time for ${post.platform}.`;
    } else if (post.postedAt.getTime() > Date.now() + 5 * 60 * 1000) {
      errors.posts = `${post.platform} cannot have a future posted time.`;
    }
  });
  if (!input.originalWorkAccepted) errors.originalWorkAccepted = "Confirm that this is your original work.";
  if (!input.rulesAccepted) errors.rulesAccepted = "Accept the campaign rules before submitting.";

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    value: {
      posts,
      sourceVideoUrl: String(input.sourceVideoUrl || ""),
      sourceVideoName: sanitizePlainText(input.sourceVideoName, { maxLength: 180 }),
      sourceTimestamp: sanitizePlainText(input.sourceTimestamp, { maxLength: 50 }),
      originalWorkAccepted: Boolean(input.originalWorkAccepted),
      rulesAccepted: Boolean(input.rulesAccepted),
    },
  };
}

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value) {
  const date = toDate(value);
  if (!date) return "Not set";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function humanizeStatus(value = "") {
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
